import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaSave, FaArrowLeft, FaShieldAlt } from 'react-icons/fa';
import { getPermissions, createRole, assignPermissionsToRole, clearState } from '../../../redux/Slices/authSlice';
import { useTitle } from '../../../shared/context/TitleContext';
import { useTranslation } from 'react-i18next';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';

import { Link, useNavigate } from 'react-router-dom';

const AddRole = () => {
  const { t } = useTranslation('global');
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { permissions, permissionsPagination, loading, error, success } = useSelector((state) => state.auth);

  const [roleData, setRoleData] = useState({
    name_en: '',
    name_ar: '',
    permission_id: []
  });

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setTitle('sidenav.addRole');
    dispatch(clearState()); // تنظيف الـ success state القديم
    dispatch(getPermissions(currentPage));
  }, [dispatch, setTitle, currentPage]);

  useEffect(() => {
    // تنظيف الـ success state فوراً عند دخول الصفحة
    if (success) {
      dispatch(clearState());
      return; // لا نعرض التوست إذا كان هذا success قديم
    }
    if (error) {
      showErrorToast(error);
    }
  }, [success, error, dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRoleData(prev => ({ ...prev, [name]: value }));
  };

  const handlePermissionChange = (permissionId) => {
    setRoleData(prev => ({
      ...prev,
      permission_id: prev.permission_id.includes(permissionId)
        ? prev.permission_id.filter(id => id !== permissionId)
        : [...prev.permission_id, permissionId]
    }));
  };

  const handleSelectAll = () => {
    const allPermissionIds = permissions.map(p => p.id);
    setRoleData(prev => ({
      ...prev,
      permission_id: prev.permission_id.length === allPermissionIds.length ? [] : allPermissionIds
    }));
  };

  useEffect(() => {
    if (success) {
      showSuccessToast(success, {
        position: "top-right",
        rtl: true,
        autoClose: 2000,
        onClose: () => {
          navigate('/admin-permissions');
        }
      });
    }
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // تنظيف أي رسائل قديمة قبل الإرسال
    dispatch(clearState());
    
    if (!roleData.name_ar?.trim()) {
      showErrorToast('يرجى إدخال الاسم بالعربية');
      return;
    }
    
    if (!roleData.name_en?.trim()) {
      showErrorToast('يرجى إدخال الاسم بالإنجليزية');
      return;
    }
    
    if (roleData.permission_id.length === 0) {
      showErrorToast('يرجى اختيار صلاحية واحدة على الأقل');
      return;
    }
    
    try {
      // إنشاء الدور أولاً
      const roleCreateData = {
        name_en: roleData.name_en,
        name_ar: roleData.name_ar
      };
      
      const createResult = await dispatch(createRole(roleCreateData)).unwrap();
      
      // ربط الصلاحيات بالدور بعد إنشائه
      if (createResult.data && createResult.data.id && roleData.permission_id.length > 0) {
        await dispatch(assignPermissionsToRole({
          roleId: createResult.data.id,
          permissions: roleData.permission_id
        })).unwrap();
      }
    } catch (error) {
      // سيتم التعامل مع الخطأ في useEffect
    }
  };

  const handlePageChange = (page) => {
    if (loading) return; // منع النقر أثناء التحميل
    if (page === currentPage) return; // منع النقر على نفس الصفحة
    setCurrentPage(page);
  };

  const LoadingSpinner = () => (
    <div className="spinner-border text-primary" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
      <span className="visually-hidden">Loading...</span>
    </div>
  );

  const Pagination = () => {
    if (!permissionsPagination || permissionsPagination.last_page <= 1) return null;

    const pages = [];
    const { current_page, last_page } = permissionsPagination;

    // زر السابق - ثابت دائماً
    pages.push(
      <button
        key="prev"
        className={`btn btn-sm mx-1 ${current_page > 1 ? 'btn-outline-primary' : 'btn-outline-secondary'}`}
        onClick={() => current_page > 1 && handlePageChange(current_page - 1)}
        disabled={current_page <= 1 || loading}
      >
        السابق
      </button>
    );

    // عرض جميع الصفحات المتاحة
    for (let i = 1; i <= last_page; i++) {
      pages.push(
        <button
          key={i}
          className={`btn btn-sm mx-1 ${i === current_page ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => handlePageChange(i)}
          disabled={loading}
        >
          {i}
        </button>
      );
    }

    // زر التالي - ثابت دائماً
    pages.push(
      <button
        key="next"
        className={`btn btn-sm mx-1 ${current_page < last_page ? 'btn-outline-primary' : 'btn-outline-secondary'}`}
        onClick={() => current_page < last_page && handlePageChange(last_page)}
        disabled={current_page >= last_page || loading}
      >
        التالي
      </button>
    );

    return (
      <div className="d-flex justify-content-center mt-3">
        {pages}
      </div>
    );
  };

  return (
    <div className="container-fluid py-4">
      
      
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex align-items-center mb-3">
            <Link to="/roles" className="text-decoration-none mx-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn btn-outline-secondary btn-sm"
                style={{ borderRadius: '8px' }}
              >
                <FaArrowLeft className="mx-2" />
                العودة
              </motion.button>
            </Link>
            <h4 className="mb-0 fw-bold" style={{ color: '#184194', fontSize: '1.1rem' }}>
              <FaShieldAlt className="mx-2" />
              إضافة صلاحية جديدة
            </h4>
          </div>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-lg-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="card shadow-lg border-0"
            style={{ borderRadius: '15px' }}
          >
            <div className="card-header bg-white border-0" style={{ padding: '2rem' }}>
              <h4 className="text-center mb-0 fw-bold" style={{ color: '#184194', fontSize: '1.3rem' }}>
                بيانات الصلاحية الجديدة
              </h4>
            </div>

            <div className="card-body" style={{ padding: '10px 30px' }}>
              <form onSubmit={handleSubmit}>
                {/* Role Names */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-medium">الاسم بالعربية</label>
                    <input
                      type="text"
                      name="name_ar"
                      value={roleData.name_ar}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="أدخل الاسم بالعربية"
                      required
                      style={{ borderRadius: '10px' }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">الاسم بالإنجليزية</label>
                    <input
                      type="text"
                      name="name_en"
                      value={roleData.name_en}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Enter name in English"
                      required
                      style={{ borderRadius: '10px' }}
                    />
                  </div>
                </div>

                {/* Permissions Selection */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <label className="form-label fw-medium mb-0">الصلاحيات</label>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleSelectAll}
                      style={{ borderRadius: '8px' }}
                    >
                      {roleData.permission_id.length === permissions.length ? 'إلغاء الكل' : 'اختيار الكل'}
                    </button>
                  </div>

                  {loading ? (
                    <div className="text-center py-4">
                      <LoadingSpinner />
                      <p className="mt-3 text-muted">جاري تحميل الصلاحيات...</p>
                    </div>
                  ) : (
                    <div className="row">
                      {permissions.map((permission) => (
                        <div key={permission.id} className="col-md-6 col-lg-4 mb-3">
                          <div className="form-check d-flex align-items-center" style={{ paddingLeft: '0' }}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`permission-${permission.id}`}
                              checked={roleData.permission_id.includes(permission.id)}
                              onChange={() => handlePermissionChange(permission.id)}
                              style={{ 
                                marginTop: '0',
                                marginRight: '20px',
                                marginLeft: '10px',
                                transform: 'scale(1.3)'
                              }}
                            />
                            <label 
                              className="form-check-label" 
                              htmlFor={`permission-${permission.id}`} 
                              style={{ 
                                marginBottom: '0', 
                                cursor: 'pointer',
                                marginLeft: '12px',
                                fontSize: '1.05rem',
                                fontWeight: '600',
                                letterSpacing: '0.4px'
                              }}
                            >
                              {permission.name}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <Pagination />
                </div>

                {/* Selected Permissions Count */}
                {roleData.permission_id.length > 0 && (
                  <div className="alert alert-info mb-4">
                    <strong>تم اختيار {roleData.permission_id.length} صلاحية</strong>
                  </div>
                )}

                {/* Submit Button */}
                <div className="text-center mb-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="btn text-white px-5 py-2 fw-medium"
                    style={{
                      backgroundColor: '#184194',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '1rem'
                    }}
                    disabled={loading}
                  >
                    {loading ? <LoadingSpinner /> : (
                      <>
                        <FaSave className="mx-2" />
                        حفظ الصلاحية
                      </>
                    )}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AddRole;
